// src/components/admin/CampaignFrequencyControl.jsx
// Reusable component for controlling campaign frequency across email, WhatsApp, and push notifications

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';

function CampaignFrequencyControl({ salonId, campaignType }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [frequencySettings, setFrequencySettings] = useState({
    maxFrequency: 10,
    interval: 'week'
  });

  // Map campaign types to display names
  const campaignTypeNames = {
    email: 'Email',
    whatsapp: 'WhatsApp',
    push: 'Push Notification'
  };

  const displayName = campaignTypeNames[campaignType] || campaignType;

  // Load existing frequency settings
  useEffect(() => {
    const loadFrequencySettings = async () => {
      if (!salonId) return;

      try {
        setLoading(true);
        const settingsRef = doc(db, 'salons', salonId);
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          const campaignFrequency = data.campaignFrequency || {};
          const typeSettings = campaignFrequency[campaignType];

          if (typeSettings) {
            setFrequencySettings({
              maxFrequency: typeSettings.maxFrequency || 10,
              interval: typeSettings.interval || 'week'
            });
          }
        }
      } catch (err) {
        console.error('Error loading frequency settings:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFrequencySettings();
  }, [salonId, campaignType]);

  // Save frequency settings
  const handleSaveFrequencySettings = async () => {
    if (!salonId) {
      setError('Salon ID is required');
      return;
    }

    if (frequencySettings.maxFrequency < 1 || frequencySettings.maxFrequency > 100) {
      setError('Maximum frequency must be between 1 and 100');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const settingsRef = doc(db, 'salons', salonId);
      const settingsDoc = await getDoc(settingsRef);

      const currentData = settingsDoc.exists() ? settingsDoc.data() : {};
      const currentCampaignFrequency = currentData.campaignFrequency || {};

      // Update the specific campaign type frequency
      const updatedCampaignFrequency = {
        ...currentCampaignFrequency,
        [campaignType]: {
          maxFrequency: parseInt(frequencySettings.maxFrequency),
          interval: frequencySettings.interval,
          updatedAt: new Date().toISOString()
        }
      };

      await setDoc(settingsRef, {
        campaignFrequency: updatedCampaignFrequency,
        updatedAt: serverTimestamp()
      }, { merge: true });

      setSuccess(`${displayName} frequency settings saved successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving frequency settings:', err);
      setError(`Failed to save ${displayName.toLowerCase()} frequency settings: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleMaxFrequencyChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 100) {
      setFrequencySettings(prev => ({ ...prev, maxFrequency: numValue }));
    }
  };

  const handleIntervalChange = (value) => {
    setFrequencySettings(prev => ({ ...prev, interval: value }));
  };

  return (
    <div className="frequency-control-section">
      <h3>{displayName} Campaign Frequency</h3>
      <p className="frequency-description">
        Set the maximum number of {displayName.toLowerCase()} messages a customer can receive from your business within a specified time period to prevent message fatigue and improve customer experience.
      </p>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="frequency-settings">
        <div className="form-group">
          <label htmlFor={`${campaignType}MaxFrequency`}>
            Maximum {displayName.toLowerCase()}s per customer
          </label>
          <input
            type="number"
            id={`${campaignType}MaxFrequency`}
            min="1"
            max="100"
            value={frequencySettings.maxFrequency}
            onChange={(e) => handleMaxFrequencyChange(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor={`${campaignType}FrequencyInterval`}>
            Within
          </label>
          <select
            id={`${campaignType}FrequencyInterval`}
            value={frequencySettings.interval}
            onChange={(e) => handleIntervalChange(e.target.value)}
            disabled={loading}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>

        <button
          type="button"
          className="save-frequency-button"
          onClick={handleSaveFrequencySettings}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Frequency Settings'}
        </button>
      </div>

      <div className="frequency-helper-text">
        <strong>Example:</strong> Setting "{frequencySettings.maxFrequency} {displayName.toLowerCase()}s per {frequencySettings.interval}" means each customer will receive no more than {frequencySettings.maxFrequency} {displayName.toLowerCase()} messages from your business within any {frequencySettings.interval === 'day' ? '24-hour' : frequencySettings.interval === 'week' ? '7-day' : '30-day'} period.
      </div>

      <div className="frequency-helper-text">
        <strong>Note:</strong> This limit applies only to PROMOTIONAL CAMPAIGNS that a customer receives.
      </div>
    </div>
  );
}

export default CampaignFrequencyControl;
